import type { Session } from 'next-auth';
import { signOut } from 'next-auth/react';

// Type guard to check if session has idToken
function hasIdToken(session: any): session is { idToken: string } {
  return typeof session?.idToken === 'string';
}

/**
 * Check if SSO is enabled for the current application
 */
export const isSSOEnabled = (): boolean => {
  return process.env.NEXT_PUBLIC_SSO_ENABLED === 'true';
};

/**
 * Perform logout for both SSO and traditional users
 * Handles the complete logout flow including:
 * - Clearing local storage and session storage
 * - Calling Commercetools logout
 * - Signing out from NextAuth (for SSO users)
 * - Redirecting to identity provider logout (for SSO users)
 * - Redirecting to login page (for traditional users)
 * 
 * @param session - NextAuth session object
 * @param logout - Commercetools logout function
 * @param router - Next.js router instance (optional)
 */
export async function performLogout(
  session: Session | null, 
  logout: () => Promise<void>, 
  router?: any
): Promise<void> {
  // Clear storage first
  localStorage.clear();
  sessionStorage.clear();
  
  console.log('[Logout] Starting logout process...');

  try {
    // Call Commercetools logout first
    await logout();
    
    // Always try to get logout URL from our API first
    // The API will determine if this is an SSO session or not (hybrid mode support)
    const response = await fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (response.ok) {
      const { logoutUrl } = await response.json();
      
      // Sign out from NextAuth first (clears session)
      await signOut({ redirect: false });
      
      // Check if we got an SSO logout URL or just a login page
      if (!logoutUrl.endsWith('/login')) {
        // This is an SSO logout URL - user has SSO session
        console.log('[SSO] Redirecting to Identity Provider logout:', logoutUrl);
        window.location.href = logoutUrl;
      } else {
        // This is just a login page - traditional logout
        console.log('[Traditional] Redirecting to login page');
        window.location.href = logoutUrl;
      }
      return;
    } else {
      console.warn('[Logout] API failed, falling back to NextAuth signOut');
      await signOut({ callbackUrl: '/login', redirect: true });
      return;
    }
  } catch (error) {
    console.error('[Logout] Error during logout:', error);
    await signOut({ callbackUrl: '/login', redirect: true });
    return;
  }
}

/**
 * Clear service workers and caches
 * Utility function to clean up browser storage during logout
 */
export const clearServiceWorkers = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(registration => registration.unregister())
      );

      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    } catch (error) {
      console.warn('Failed to clear service workers:', error);
    }
  }
};